// import { featuredContents } from "@/composable/featuredContent";
import { http } from "@/composable/httpService";
import { useHandleErrorStatus } from "@/composable/useHandleErrorStatus";
import { defineStore } from "pinia";
import { computed } from "vue";
import { ref } from "vue";

export const useHomeFeaturedContentStore = defineStore(
  "homeFeaturedContentStore",
  () => {
    const contents = ref();
    const fetchedNewContents = ref();

    const playlist = computed(() => contents.value?.playlists.data);

    // Computed property that merges new contents with progress data
    const newContents = computed(() => {

      if (!fetchedNewContents.value?.new_contents) return [];

      const baseContents = fetchedNewContents.value.new_contents;

      // If no progress data yet, return base contents
      if (!newContentsProgress.value || newContentsProgress.value.length === 0) {
        return baseContents;
      }

      // Create a map of progress data for quick lookup
      const progressMap = new Map(
        newContentsProgress.value.map(item => [item.id, item.progress])
      );

      // Merge progress data with contents
      return baseContents?.map(content => ({
        ...content,
        completed_percentage: progressMap.get(content.id) ?? content.completed_percentage ?? 0
      }));
    });

    const loadingContents = ref(false);
    const loadingNewContentsProgress = ref(false);
    const newContentsProgress = ref([]);
    const playlistPage = ref(1);
    const perPage = ref(40);
    const isLastPage = ref(false);
    const initialLoading = ref(true);
    const showLoadingFullScreen = ref(false);
    const initialNewContentsLoading = ref(true);
    const sortBy = ref("");

    const params = computed(() => {
      return {
        page: playlistPage.value,
        per_page: perPage.value,
        sort_by: sortBy.value,
        content_type: 'learning-paths-home',
      };
    });

    const handleFetchFeaturedContent = async () => {
      try {
        loadingContents.value = true;
        const { data } = await http().get(
          "/v3/learning-paths-and-new-contents-v2",
          {
            params: params.value,
          }
        );
        if (playlistPage.value === 1) {
          contents.value = data;
        }
        if (playlistPage.value > 1 && data.playlists.data.length) {
          contents.value.playlists.data = contents.value.playlists.data.concat(data.playlists.data);
        }

        if (contents.value) {
          //
          isLastPage.value =
            contents.value.playlists.last_page === playlistPage.value;
        }
      } catch (error) {
        useHandleErrorStatus(error);
      } finally {
        loadingContents.value = false;
        initialLoading.value = false;
      }
    };

    const handleNewContents = async () => {
      try {
        const newParams = {
          ...params.value,
          content_type: 'new-contents'
        };

        const { data } = await http().get(
          "/v3/learning-paths-and-new-contents-v2",
          {
            params: newParams,
          }
        );

        fetchedNewContents.value = data;

      } catch (error) {
        useHandleErrorStatus(error);
      } finally {
        initialNewContentsLoading.value = false;
      }
    };

    const handleNewContentsProgress = async () => {
      try {
        loadingNewContentsProgress.value = true;
        const { data } = await http().get(`/v3/progress-calculation/new-contents`);
        console.log('Progress data received:', data);

        // Handle the nested structure from your API response
        newContentsProgress.value = data.new_contents_progress || [];

      } catch (error) {
        useHandleErrorStatus(error);
        console.error('Error fetching progress:', error);
      } finally {
        loadingNewContentsProgress.value = false;
      }
    };

    return {
      contents,
      playlist,
      newContents,
      isLastPage,
      playlistPage,
      perPage,
      loadingContents,
      initialLoading,
      initialNewContentsLoading,
      loadingNewContentsProgress,
      newContentsProgress,
      showLoadingFullScreen,
      handleFetchFeaturedContent,
      handleNewContents,
      handleNewContentsProgress,
      sortBy,
    };
  }
);
